/**
 * VA Alert Overlay — fires when a trip needs a VA decision.
 *
 * Mounted at the AdminDashboard level so it works on EVERY page the VA is
 * looking at. Polls /api/admin/va-ops/pending-count every 5s. When the
 * count increments (or a stale trip crosses the alarm threshold) it fires
 * FOUR sensory signals in parallel so nothing is missed:
 *
 *   1. Haptic feedback     — navigator.vibrate([200,100,200])   (mobile)
 *   2. Sound alert         — Web Audio API tone pair (no external file)
 *   3. On-screen banner    — a red pulsing overlay at the top of the app
 *   4. Browser notification — desktop toast when tab is backgrounded
 *
 * Also renders a persistent pulsing badge in the top nav (via a portal-free
 * fixed-position element) showing "N trips pending — oldest Xm ago".
 *
 * "Acknowledge" dismisses the banner but the badge stays visible until the
 * count actually drops. That way the VA can silence the alarm without
 * losing awareness the queue is still hot.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";

const POLL_MS = 5000;
const STALE_ALARM_LOUDER_AFTER_S = 300;   // 5 min → alarm re-fires

const C = {
  red: "#B91C1C",
  redSoft: "#FEE2E2",
  amber: "#B45309",
  green: "#047857",
  ink: "#0F172A",
  slate: "#475569",
  bone: "#F4F1EA",
};

// ─────────────────────────────────────────────────────────────
// Web-Audio synthesizer — no external audio file needed.
// Two-tone attention chime. Users can silence via "Ack".
// ─────────────────────────────────────────────────────────────

function playAlertChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const notes = [
      { freq: 880, start: 0.00, dur: 0.18 },  // A5
      { freq: 1174, start: 0.20, dur: 0.28 }, // D6
    ];
    notes.forEach(n => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = n.freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const t0 = ctx.currentTime + n.start;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.28, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + n.dur);
      osc.start(t0);
      osc.stop(t0 + n.dur + 0.02);
    });
    // Close the context after all tones play so we don't leak
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 800);
  } catch {
    /* silent */
  }
}

function haptic() {
  try {
    if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
  } catch { /* ignore */ }
}

function browserNotify(title, body) {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      new Notification(title, { body, icon: "/logo192.png", tag: "va-alert" });
    }
  } catch { /* ignore */ }
}

// ─────────────────────────────────────────────────────────────
// Sound-on toggle (browsers won't play audio until first user gesture)
// ─────────────────────────────────────────────────────────────

function useSoundEnabled() {
  const KEY = "astat.va_alert.sound_enabled";
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(KEY) !== "false"; } catch { return true; }
  });
  const set = useCallback((v) => {
    setEnabled(v);
    try { localStorage.setItem(KEY, String(v)); } catch { /* ignore */ }
  }, []);
  return [enabled, set];
}

// ─────────────────────────────────────────────────────────────
// Main overlay
// ─────────────────────────────────────────────────────────────

export default function VaAlertOverlay() {
  const [count, setCount] = useState(0);
  const [oldestSec, setOldestSec] = useState(0);
  const [banner, setBanner] = useState(false);
  const [acked, setAcked] = useState(false);
  const [soundOn, setSoundOn] = useSoundEnabled();
  const [activeCalls, setActiveCalls] = useState([]);
  const lastCountRef = useRef(0);
  const lastAlarmRef = useRef(0);      // last time we re-fired the alarm (ms epoch)

  const fireAlert = useCallback((title, body) => {
    haptic();
    if (soundOn) playAlertChime();
    browserNotify(title, body);
    setBanner(true);
    setAcked(false);
    lastAlarmRef.current = Date.now();
  }, [soundOn]);

  const poll = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/admin/va-ops/pending-count");
      const nextCount = Number(data?.pending || 0);
      const nextOldest = Number(data?.oldest_wait_seconds || 0);
      // Trigger conditions:
      //   A) count went up  → new trip appeared, always alert
      //   B) count > 0 AND oldest > 5 min AND we haven't re-alarmed for 90s → re-fire
      if (nextCount > lastCountRef.current) {
        fireAlert(
          `${nextCount} trip${nextCount === 1 ? "" : "s"} waiting for you`,
          "A new offer has been ingested and needs your decision."
        );
      } else if (
        nextCount > 0 &&
        nextOldest >= STALE_ALARM_LOUDER_AFTER_S &&
        Date.now() - lastAlarmRef.current > 90_000
      ) {
        fireAlert(
          `Stale queue: ${nextCount} pending, oldest ${Math.floor(nextOldest / 60)}m`,
          "A VA decision has been pending longer than the SLA. Take action."
        );
      }
      lastCountRef.current = nextCount;
      setCount(nextCount);
      setOldestSec(nextOldest);
    } catch {
      /* silent — never spam errors from a polling background loop */
    }
  }, [fireAlert]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [poll]);

  const pollCalls = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/floor/calls/active");
      setActiveCalls(Array.isArray(data) ? data : []);
    } catch {
      /* silent — call pill just stays empty on transient errors */
    }
  }, []);

  useEffect(() => {
    pollCalls();
    const id = setInterval(pollCalls, 5000);
    return () => clearInterval(id);
  }, [pollCalls]);

  // Prompt for notification permission once, on first user click anywhere.
  useEffect(() => {
    const askOnce = () => {
      try {
        if ("Notification" in window && Notification.permission === "default") {
          Notification.requestPermission().catch(() => {});
        }
      } catch { /* ignore */ }
      window.removeEventListener("click", askOnce);
    };
    window.addEventListener("click", askOnce, { once: true });
    return () => window.removeEventListener("click", askOnce);
  }, []);

  const mins = Math.floor(oldestSec / 60);
  const isStale = oldestSec >= STALE_ALARM_LOUDER_AFTER_S;
  const alarmColor = isStale ? C.red : C.amber;
  const hasActiveCalls = activeCalls.length > 0;

  // The banner below is `position: fixed` across the full top of the
  // viewport — without this, it visually sits ON TOP of whatever's at the
  // top of the current admin page (e.g. a settings toggle) and swallows
  // the first click. Push page content down by the banner's height while
  // it's showing instead of just covering it.
  useEffect(() => {
    if (banner && !acked) {
      document.body.style.paddingTop = "64px";
    } else {
      document.body.style.paddingTop = "";
    }
    return () => { document.body.style.paddingTop = ""; };
  }, [banner, acked]);

  return (
    <>
      {/* Persistent call-status pill — visible on every admin page */}
      {hasActiveCalls && (
        <Link
          to="/admin/live-ops"
          data-testid="call-status-pill"
          title={activeCalls.map(c => `${c.direction === "inbound" ? "Inbound" : "Outbound"} · ${c.purpose || "call"}`).join("\n")}
          style={{
            position: "fixed",
            top: 12,
            left: 12,
            zIndex: 60,
            padding: "8px 14px",
            borderRadius: 999,
            background: C.green,
            color: "#fff",
            fontWeight: 800,
            fontSize: 13,
            textDecoration: "none",
            boxShadow: "0 8px 20px rgba(4,120,87,0.35)",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            animation: "va-alert-pulse 1.8s ease-in-out infinite",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: "#fff",
              boxShadow: "0 0 0 4px rgba(255,255,255,0.32)",
            }}
          />
          {activeCalls.length} active call{activeCalls.length === 1 ? "" : "s"}
        </Link>
      )}

      {count === 0 ? null : (
        <>
      {/* Sticky pulsing badge — always visible until count drops to 0 */}
      <Link
        to="/admin/live-ops?filter=pending"
        data-testid="va-alert-badge"
        style={{
          position: "fixed",
          top: 12,
          right: 12,
          zIndex: 60,
          padding: "8px 14px",
          borderRadius: 999,
          background: alarmColor,
          color: "#fff",
          fontWeight: 800,
          fontSize: 13,
          textDecoration: "none",
          boxShadow: "0 8px 20px rgba(185,28,28,0.35)",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          animation: "va-alert-pulse 1.4s ease-in-out infinite",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 0 0 4px rgba(255,255,255,0.32)",
          }}
        />
        {count} pending {isStale ? `· stale ${mins}m` : `· oldest ${mins}m`}
      </Link>

      {/* Loud banner — dismissible with Acknowledge */}
      {banner && !acked && (
        <div
          data-testid="va-alert-banner"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 55,
            padding: "16px 24px",
            background: alarmColor,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
            animation: "va-alert-flash 0.9s ease-in-out 3",
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: "-0.01em" }}>
            {isStale ? "STALE QUEUE" : "NEW OFFER"}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, opacity: 0.94 }}>
            {count} trip{count === 1 ? "" : "s"} pending · oldest {mins}m
          </div>
          <Link
            to="/admin/live-ops?filter=pending"
            data-testid="va-alert-goto"
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              background: "#fff",
              color: alarmColor,
              fontSize: 13,
              fontWeight: 800,
              textDecoration: "none",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Open queue →
          </Link>
          <button
            type="button"
            data-testid="va-alert-ack"
            onClick={(e) => { e.preventDefault(); setAcked(true); }}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              background: "transparent",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.6)",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            Ack
          </button>
          <button
            type="button"
            data-testid="va-alert-sound-toggle"
            onClick={(e) => { e.preventDefault(); setSoundOn(!soundOn); }}
            title={soundOn ? "Mute sound" : "Enable sound"}
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              background: "transparent",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.6)",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {soundOn ? "🔊" : "🔇"}
          </button>
        </div>
      )}
        </>
      )}

      {/* Keyframes — inlined so we don't need a stylesheet edit */}
      <style>{`
        @keyframes va-alert-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 8px 20px rgba(185,28,28,0.35); }
          50%      { transform: scale(1.06); box-shadow: 0 10px 28px rgba(185,28,28,0.55); }
        }
        @keyframes va-alert-flash {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.85; }
        }
      `}</style>
    </>
  );
}
